import { useState } from "react";
import {ChevronDown} from 'lucide-react';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      q: "Is registration free?",
      a: "Yes — free until Feb 29, 2026. From Mar 1, 2026 it will be $5, and from Jun 1, 2026 it will be $10."
    },
    {
      q: "Do I need NCLEX already?",
      a: "No. You can register as a student or preparing nurse. We'll guide you through NCLEX/CGFNS/Dataflow/CBT-OSCE."
    },
    {
      q: "Will recruiters see my data?",
      a: "Not until you opt in. Your profile remains private by default. You can toggle recruiter visibility when ready."
    },
    {
      q: "Which countries can I work in?",
      a: "Multiple pathways: U.S., UK/Ireland, GCC, Canada, and select EU countries — see the Countries page for details."
    },
    {
      q: "How do I join the community?",
      a: "Join our WhatsApp group from the Resources or Contact pages for updates and mentorship."
    },
    {
      q: "What if I fail NCLEX the first time?",
      a: "We provide resources for retakes and connect you with mentors. Many nurses pass on the second attempt with proper guidance."
    },
    {
      q: "How do I know a recruiter is legitimate?",
      a: "We screen and verify all recruiters through background checks and industry references. Only ethical, verified recruiters can contact you. If anything feels off, contact us immediately."
    },
    {
      q: "Is my personal data safe?",
      a: "Yes. We use encryption, never share data without consent, and comply with international privacy standards. See our Privacy Policy."
    },
    {
      q: "I'm already in the U.S./UK/GCC. Can I use Nursing Hive?",
      a: "Yes! Update your profile and explore state endorsements, license transfers, or opportunities in other countries."
    },
    {
      q: "Once I'm verified, how quickly can I get matched with recruiters?",
      a: "Usually 1–2 weeks after marking recruiter visibility active. Our average matching time is 10 days for nurses with 3+ years experience."
    },
    {
      q: "How long does NCLEX/CGFNS/Dataflow take?",
      a: "Typically 3–6 months to prepare, then 2–4 weeks to pass the exam. See our Resources page for detailed timelines by country."
    },
    {
      q: "I'm a recruiter. How do I find nurses?",
      a: "Contact us at hello@nursinghive.com to partner with us. We connect verified recruiters with qualified nurses who match their hiring needs."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-teal-50">
            Everything you need to know about Nursing Hive
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-8">
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-teal-600 flex-shrink-0 transition-transform ${
                      openIndex === index ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openIndex === index && (
                  <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-teal-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Still have questions?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            We're here to help. Reach out to our team anytime.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-block bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
            >
              Contact Us
            </a>
            <a
              href="/signup"
              className="inline-block bg-white text-teal-600 px-8 py-3 rounded-lg font-semibold border-2 border-teal-600 hover:bg-teal-50 transition-colors"
            >
              Register Free
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
